/**
 * @barmetvault/shared — Permission constants
 *
 * Role-based access control matrix shared between API and frontend.
 */

import { UserRole } from '../types/user';

// ─── Permission Enum ───────────────────────────────────────────────────────

export enum Permission {
  VIEW_METADATA = 'VIEW_METADATA',
  USE = 'USE',                       // autofill / use without revealing
  REVEAL = 'REVEAL',                 // show plaintext in UI
  COPY = 'COPY',                     // copy to clipboard
  EDIT = 'EDIT',
  CREATE_VERSION = 'CREATE_VERSION',
  SHARE = 'SHARE',
  MANAGE_PERMISSIONS = 'MANAGE_PERMISSIONS',
  EXPORT = 'EXPORT',
  DELETE = 'DELETE',
  RESTORE = 'RESTORE',
  ROTATE = 'ROTATE',
}

// ─── Resource Types ────────────────────────────────────────────────────────

export type ResourceType = 'VAULT_ITEM' | 'VAULT' | 'ORG';

// ─── Role-Permission Matrix ────────────────────────────────────────────────

const VAULT_ITEM_PERMISSIONS: Record<UserRole, Set<Permission>> = {
  [UserRole.OWNER]: new Set(Object.values(Permission)),
  [UserRole.SECURITY_ADMIN]: new Set([
    Permission.VIEW_METADATA,
    Permission.REVEAL,
    Permission.COPY,
    Permission.ROTATE,
    Permission.DELETE,
    Permission.RESTORE,
    Permission.EXPORT,
    Permission.MANAGE_PERMISSIONS,
  ]),
  [UserRole.ADMIN]: new Set([
    Permission.VIEW_METADATA,
    Permission.USE,
    Permission.REVEAL,
    Permission.COPY,
    Permission.EDIT,
    Permission.CREATE_VERSION,
    Permission.SHARE,
    Permission.DELETE,
    Permission.RESTORE,
    Permission.EXPORT,
    Permission.ROTATE,
  ]),
  [UserRole.MANAGER]: new Set([
    Permission.VIEW_METADATA,
    Permission.USE,
    Permission.REVEAL,
    Permission.COPY,
    Permission.EDIT,
    Permission.CREATE_VERSION,
    Permission.SHARE,
    Permission.EXPORT,
    Permission.ROTATE,
  ]),
  [UserRole.MEMBER]: new Set([
    Permission.VIEW_METADATA,
    Permission.USE,
    Permission.REVEAL,
    Permission.COPY,
    Permission.EDIT,
    Permission.CREATE_VERSION,
  ]),
  [UserRole.AUDITOR]: new Set([
    Permission.VIEW_METADATA,
  ]),
  [UserRole.SERVICE_ACCOUNT]: new Set([
    Permission.VIEW_METADATA,
    Permission.USE,
    Permission.REVEAL,
    Permission.COPY,
  ]),
};

const VAULT_PERMISSIONS: Record<UserRole, Set<Permission>> = {
  [UserRole.OWNER]: new Set(Object.values(Permission)),
  [UserRole.SECURITY_ADMIN]: new Set([
    Permission.VIEW_METADATA,
    Permission.MANAGE_PERMISSIONS,
    Permission.DELETE,
    Permission.RESTORE,
    Permission.EXPORT,
  ]),
  [UserRole.ADMIN]: new Set([
    Permission.VIEW_METADATA,
    Permission.EDIT,
    Permission.SHARE,
    Permission.MANAGE_PERMISSIONS,
    Permission.DELETE,
    Permission.RESTORE,
    Permission.EXPORT,
  ]),
  [UserRole.MANAGER]: new Set([
    Permission.VIEW_METADATA,
    Permission.EDIT,
    Permission.SHARE,
    Permission.EXPORT,
  ]),
  [UserRole.MEMBER]: new Set([
    Permission.VIEW_METADATA,
    Permission.USE,
    Permission.REVEAL,
    Permission.COPY,
  ]),
  [UserRole.AUDITOR]: new Set([
    Permission.VIEW_METADATA,
  ]),
  [UserRole.SERVICE_ACCOUNT]: new Set([
    Permission.VIEW_METADATA,
    Permission.USE,
  ]),
};

const ORG_PERMISSIONS: Record<UserRole, Set<Permission>> = {
  [UserRole.OWNER]: new Set(Object.values(Permission)),
  [UserRole.SECURITY_ADMIN]: new Set([
    Permission.VIEW_METADATA,
    Permission.MANAGE_PERMISSIONS,
    Permission.ROTATE,
    Permission.DELETE,
    Permission.RESTORE,
    Permission.EXPORT,
  ]),
  [UserRole.ADMIN]: new Set([
    Permission.VIEW_METADATA,
    Permission.EDIT,
    Permission.MANAGE_PERMISSIONS,
    Permission.EXPORT,
  ]),
  [UserRole.MANAGER]: new Set([
    Permission.VIEW_METADATA,
    Permission.EDIT,
  ]),
  [UserRole.MEMBER]: new Set([
    Permission.VIEW_METADATA,
  ]),
  [UserRole.AUDITOR]: new Set([
    Permission.VIEW_METADATA,
  ]),
  [UserRole.SERVICE_ACCOUNT]: new Set([
    Permission.VIEW_METADATA,
  ]),
};

/**
 * Map of resource type to its role-permission matrix.
 */
const ROLE_PERMISSIONS: Record<ResourceType, Record<UserRole, Set<Permission>>> = {
  VAULT_ITEM: VAULT_ITEM_PERMISSIONS,
  VAULT: VAULT_PERMISSIONS,
  ORG: ORG_PERMISSIONS,
};

// ─── Helper ────────────────────────────────────────────────────────────────

/**
 * Check whether a given role has a specific permission on a resource type.
 */
export function hasPermission(
  role: UserRole,
  resourceType: ResourceType,
  permission: Permission,
): boolean {
  const matrix = ROLE_PERMISSIONS[resourceType];
  if (!matrix) return false;
  const perms = matrix[role];
  if (!perms) return false;
  return perms.has(permission);
}

export { ROLE_PERMISSIONS };
